"use client";

// react
import { useRef } from "react";

// anivive
import { Date, Multi, RadioList, Text } from "@anivive/ui";

// services
import { getBreeds } from "@/services/api/client/pets";
import { Pet } from "@/services/api/server/pets";

// hooks
import useAsync from "@/hooks/use-async";

// private components
import { FormPet, useWizard } from "../../context";
import Buttons from "../buttons";

type Props = {
  review?: boolean;
  onCancel?: () => void;
  pets: Pet[];
};

export default function PetInfo({ review, onCancel, pets }: Props) {
  const { state, trial, actions } = useWizard();

  const { data: breeds } = useAsync(() => getBreeds(trial.species.id), [trial.species.id]);

  const ref = useRef<HTMLFormElement | null>(null);

  const valid = ref.current?.checkValidity() ?? false;

  const getPet = (): FormPet => {
    const stateDataPet = state.data.pet;

    if (stateDataPet && "id" in stateDataPet) {
      const existingPet = pets.find(({ id }) => id === stateDataPet.id);

      if (existingPet) {
        return {
          ...existingPet,
          breeds: existingPet.breeds.map(({ id }) => id),
          species: existingPet.species.id,
        };
      }
    }

    return state.data.pet ?? ({} as FormPet);
  };

  const statePet = getPet();

  const handleOnChange = (name: string, value: unknown) => {
    actions.setData({
      pet: {
        ...statePet,
        species: trial.species.id,
        [name]: value,
      },
    });
  };

  return (
    <>
      <form ref={ref} className="col-12 col-md-6">
        <div className="mb-4">
          <p className="text-style-body1-regular mb-1">Pet Name</p>
          <Text
            disabled={review}
            required
            className="w-100"
            value={statePet ? statePet.name : ""}
            name="name"
            onChange={(value) => handleOnChange("name", value)}
          />
        </div>
        <div className="mb-4">
          <p className="text-style-body1-regular mb-2">Gender</p>
          <RadioList
            disabled={review}
            required
            value={statePet?.gender}
            options={[
              { value: "M", label: "Male" },
              { value: "F", label: "Female" },
            ]}
            name="gender"
            onChange={(value) => handleOnChange("gender", value)}
          />
        </div>
        <div className="mb-4">
          <p className="text-style-body1-regular mb-2">Is your pet sterilized?</p>
          <RadioList
            disabled={review}
            required
            value={
              typeof statePet?.sterilized === "boolean" ? String(statePet.sterilized) : undefined
            }
            options={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
            name="sterilized"
            onChange={(value) => handleOnChange("sterilized", value === "true")}
          />
        </div>
        <div className="mb-4">
          <p className="text-style-body1-regular mb-1">{trial.species.name} Breed(s)</p>
          <Multi
            className="w-100"
            disabled={review}
            value={statePet.breeds}
            onChange={(value) => handleOnChange("breeds", value)}
            placeholder="Select breed(s)"
            name="breeds"
            options={breeds?.map((b) => ({ value: b.id, label: b.name })) ?? []}
          />
        </div>
        <div className="mb-4">
          <p className="text-style-body1-regular mb-2">Adopted or Birth</p>
          <RadioList
            disabled={review}
            required
            value={statePet.celebrationType}
            options={[
              { value: "ADOPTION_DAY", label: "Adoption" },
              { value: "BIRTHDAY", label: "Birthday" },
            ]}
            name="celebrationType"
            onChange={(value) => handleOnChange("celebrationType", value)}
          />
        </div>
        <div>
          <p className="text-style-body1-regular mb-1">Celebration Day</p>
          <Date
            className="w-100"
            disabled={review}
            required
            value={statePet ? statePet.celebrationDay : ""}
            name="celebrationDay"
            onChange={(value) => handleOnChange("celebrationDay", value)}
          />
        </div>
      </form>

      {!review && <Buttons disabled={!valid} onBack={onCancel} />}
    </>
  );
}
